import { useEffect, useState, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { CustomerPortalRoutes } from './pages/customer/CustomerPortalRoutes';
import { isCustomerPortalHost } from './lib/customerHost';
import { Box, Center, Loader, Text } from '@mantine/core';
import { useAuthStore } from './stores/auth';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { AppLayout } from './components/layout/AppLayout';
import { TicketCockpitDrawer } from './components/TicketCockpitDrawer';
import { KianFlyoutRoot } from './components/KianAssistant/flyout/KianFlyoutRoot';
import { GlobalCustomerDrawer } from './pages/customer-hub/CustomerDetailDrawer';
import { ContactSuggestionsPage } from './pages/ContactSuggestionsPage';
import { AddressSuggestionsPage } from './pages/AddressSuggestionsPage';
import { ContactDirectoryPage } from './pages/ContactDirectoryPage';

const ServiceReportPage = lazy(() =>
  import('./pages/ServiceReportPage').then((m) => ({ default: m.ServiceReportPage }))
);
const DeliveryReportPage = lazy(() =>
  import('./pages/DeliveryReportPageV2').then((m) => ({ default: m.DeliveryReportPageV2 }))
);
const SubmissionsPage = lazy(() =>
  import('./pages/SubmissionsPage').then((m) => ({ default: m.SubmissionsPage }))
);
const MyTripsPage = lazy(() => import('./pages/MyTripsPage').then((m) => ({ default: m.MyTripsPage })));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then((m) => ({ default: m.ProfilePage })));
const ArticleReviewPage = lazy(() =>
  import('./pages/ArticleReviewPage').then((m) => ({ default: m.ArticleReviewPage }))
);
const PortalAccessRequestsPage = lazy(() =>
  import('./pages/PortalAccessRequestsPage').then((m) => ({ default: m.PortalAccessRequestsPage }))
);
const NewTicketPage = lazy(() =>
  import('./pages/NewTicketPage').then((m) => ({ default: m.NewTicketPage }))
);
// Tickets: Liste/Disposition/Kanban zu einer Seite mit Ansichts-Umschalter zusammengeführt.
const BriefingPage = lazy(() =>
  import('./pages/BriefingPage').then((m) => ({ default: m.BriefingPage }))
);
const StatisticsPage = lazy(() =>
  import('./pages/StatisticsPage').then((m) => ({ default: m.StatisticsPage }))
);
const TicketDeepLinkPage = lazy(() =>
  import('./pages/TicketDeepLinkPage').then((m) => ({ default: m.TicketDeepLinkPage }))
);
const TicketsHubPage = lazy(() =>
  import('./pages/TicketsHubPage').then((m) => ({ default: m.TicketsHubPage }))
);
// Native Lager-Seiten (ServiceHub-eigenes Inventar, UUID-basiert) ersetzen die
// CW-gekoppelten Inventory-Seiten. Konstanten-Namen bleiben → Routen unverändert.
const InventoryPage = lazy(() =>
  import('./pages/lager/LagerOverviewPage').then((m) => ({ default: m.LagerOverviewPage }))
);
const WarehouseBinsPage = lazy(() =>
  import('./pages/lager/LagerBinsPage').then((m) => ({ default: m.LagerBinsPage }))
);
const BinOnHandPage = lazy(() =>
  import('./pages/lager/LagerBinOnHandPage').then((m) => ({ default: m.LagerBinOnHandPage }))
);
const ArticlesPage = lazy(() =>
  import('./pages/lager/ArticlesPage').then((m) => ({ default: m.ArticlesPage }))
);
const MovementsPage = lazy(() =>
  import('./pages/lager/MovementsPage').then((m) => ({ default: m.MovementsPage }))
);
const StockIssuePage = lazy(() =>
  import('./pages/lager/StockIssuePage').then((m) => ({ default: m.StockIssuePage }))
);
const StockSearchPage = lazy(() =>
  import('./pages/lager/StockSearchPage').then((m) => ({ default: m.StockSearchPage }))
);
const StockTransferPage = lazy(() =>
  import('./pages/lager/StockTransferPage').then((m) => ({ default: m.StockTransferPage }))
);
const ValuationPage = lazy(() =>
  import('./pages/lager/ValuationPage').then((m) => ({ default: m.ValuationPage }))
);
const LowStockPage = lazy(() =>
  import('./pages/lager/LowStockPage').then((m) => ({ default: m.LowStockPage }))
);
const SerialHistoryPage = lazy(() =>
  import('./pages/lager/SerialHistoryPage').then((m) => ({ default: m.SerialHistoryPage }))
);
const StockReturnPage = lazy(() =>
  import('./pages/lager/StockReturnPage').then((m) => ({ default: m.StockReturnPage }))
);
const StockReturnReviewPage = lazy(() =>
  import('./pages/lager/StockReturnReviewPage').then((m) => ({ default: m.StockReturnReviewPage }))
);
const DeliveryOrderListPage = lazy(() =>
  import('./pages/lager/DeliveryOrderListPage').then((m) => ({ default: m.DeliveryOrderListPage }))
);
const DeliveryOrderDetailPage = lazy(() =>
  import('./pages/lager/DeliveryOrderDetailPage').then((m) => ({ default: m.DeliveryOrderDetailPage }))
);
const InventoryStartPage = lazy(() =>
  import('./pages/InventoryStartPage').then((m) => ({ default: m.InventoryStartPage }))
);
const InventorySessionPage = lazy(() =>
  import('./pages/InventorySessionPage').then((m) => ({ default: m.InventorySessionPage }))
);
const InventoryReviewPage = lazy(() =>
  import('./pages/InventoryReviewPage').then((m) => ({ default: m.InventoryReviewPage }))
);
const GoodsReceiptsListPage = lazy(() =>
  import('./pages/goodsReceipt/GoodsReceiptsListPage').then((m) => ({
    default: m.GoodsReceiptsListPage,
  }))
);
const GoodsReceiptWizardPage = lazy(() =>
  import('./pages/goodsReceipt/GoodsReceiptWizardPage').then((m) => ({
    default: m.GoodsReceiptWizardPage,
  }))
);
const PurchaseOrdersListPage = lazy(() =>
  import('./pages/purchaseOrder/PurchaseOrdersListPage').then((m) => ({
    default: m.PurchaseOrdersListPage,
  }))
);
const PurchaseOrderWizardPage = lazy(() =>
  import('./pages/purchaseOrder/PurchaseOrderWizardPage').then((m) => ({
    default: m.PurchaseOrderWizardPage,
  }))
);
const ProcurementPipelinePage = lazy(() =>
  import('./pages/procurement/ProcurementPipelinePage').then((m) => ({
    default: m.ProcurementPipelinePage,
  }))
);
const PipelineDetailPage = lazy(() =>
  import('./pages/procurement/PipelineDetailPage').then((m) => ({
    default: m.PipelineDetailPage,
  }))
);
const SubmissionArchivePage = lazy(() =>
  import('./pages/archive/SubmissionArchivePage').then((m) => ({
    default: m.SubmissionArchivePage,
  }))
);
const ArchiveRecordPage = lazy(() =>
  import('./pages/archive/ArchiveRecordPage').then((m) => ({
    default: m.ArchiveRecordPage,
  }))
);
const AdminUsersPage = lazy(() =>
  import('./pages/admin/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage }))
);
const AdminBelegePage = lazy(() =>
  import('./pages/admin/AdminBelegePage').then((m) => ({ default: m.AdminBelegePage }))
);
const OrdersPage = lazy(() =>
  import('./pages/OrdersPage').then((m) => ({ default: m.OrdersPage }))
);
const AdminSubmissionsPage = lazy(() =>
  import('./pages/admin/AdminSubmissionsPage').then((m) => ({ default: m.AdminSubmissionsPage }))
);
const AdminSettingsPage = lazy(() =>
  import('./pages/admin/settings').then((m) => ({ default: m.AdminSettingsPage }))
);
const AdminTripsPage = lazy(() =>
  import('./pages/admin/AdminTripsPage').then((m) => ({ default: m.AdminTripsPage }))
);
const AdminManualTripsPage = lazy(() =>
  import('./pages/admin/AdminManualTripsPage').then((m) => ({ default: m.AdminManualTripsPage }))
);
const AdminSyncPage = lazy(() =>
  import('./pages/admin/AdminSyncPage').then((m) => ({ default: m.AdminSyncPage }))
);
const AdminBillingJournalPage = lazy(() =>
  import('./pages/admin/AdminBillingJournalPage').then((m) => ({ default: m.AdminBillingJournalPage }))
);
const AdminWeclappMirrorPage = lazy(() =>
  import('./pages/admin/AdminWeclappMirrorPage').then((m) => ({ default: m.AdminWeclappMirrorPage }))
);
const AdminTechnicianRequestsPage = lazy(() =>
  import('./pages/admin/AdminTechnicianRequestsPage').then((m) => ({
    default: m.AdminTechnicianRequestsPage,
  }))
);
const TechnicianRequestsPage = lazy(() =>
  import('./pages/TechnicianRequestsPage').then((m) => ({ default: m.TechnicianRequestsPage }))
);
const ProjectsPage = lazy(() =>
  import('./pages/projects/ProjectsPage').then((m) => ({ default: m.ProjectsPage }))
);
const ProjectDetailPage = lazy(() =>
  import('./pages/projects/ProjectDetailPage').then((m) => ({ default: m.ProjectDetailPage }))
);
const ProjectTemplatesPage = lazy(() =>
  import('./pages/projects/ProjectTemplatesPage').then((m) => ({ default: m.ProjectTemplatesPage }))
);
const TodosPage = lazy(() =>
  import('./pages/todos/TodosPage').then((m) => ({ default: m.TodosPage }))
);
const TodoOverviewPage = lazy(() =>
  import('./pages/todos/TodoOverviewPage').then((m) => ({ default: m.TodoOverviewPage }))
);
const CalendarPage = lazy(() =>
  import('./pages/calendar/CalendarPage').then((m) => ({ default: m.CalendarPage }))
);
const TechnicianAppointmentsPage = lazy(() =>
  import('./pages/TechnicianAppointmentsPage').then((m) => ({ default: m.TechnicianAppointmentsPage }))
);
const ToolsPage = lazy(() => import('./pages/ToolsPage').then((m) => ({ default: m.ToolsPage })));
const AdminToolsPage = lazy(() =>
  import('./pages/admin/AdminToolsPage').then((m) => ({ default: m.AdminToolsPage }))
);
const TrashPage = lazy(() => import('./pages/TrashPage').then((m) => ({ default: m.TrashPage })));
const TicketTrashPage = lazy(() =>
  import('./pages/TicketTrashPage').then((m) => ({ default: m.TicketTrashPage })),
);
const InfrastructureViewerPage = lazy(() =>
  import('./pages/InfrastructureViewerPage').then((m) => ({ default: m.InfrastructureViewerPage }))
);
const CustomerHubPage = lazy(() =>
  import('./pages/customer-hub/CustomerHubPage').then((m) => ({ default: m.CustomerHubPage }))
);
const QuoteWorkflowPage = lazy(() =>
  import('./pages/customer-hub/QuoteWorkflowPage').then((m) => ({ default: m.QuoteWorkflowPage }))
);
const WarrantyWorkflowPage = lazy(() =>
  import('./pages/customer-hub/WarrantyWorkflowPage').then((m) => ({
    default: m.WarrantyWorkflowPage,
  }))
);
const OrderWorkflowPage = lazy(() =>
  import('./pages/customer-hub/OrderWorkflowPage').then((m) => ({ default: m.OrderWorkflowPage }))
);
const CustomerOrderWorkflowPage = lazy(() =>
  import('./pages/customer-hub/CustomerOrderWorkflowPage').then((m) => ({
    default: m.CustomerOrderWorkflowPage,
  }))
);
const CustomerApprovePage = lazy(() =>
  import('./pages/customer-hub/CustomerApprovePage').then((m) => ({ default: m.CustomerApprovePage }))
);
const CustomerDetailPage = lazy(() =>
  import('./pages/customer-hub/CustomerDetailPage').then((m) => ({ default: m.CustomerDetailPage }))
);
const CustomerReportsPage = lazy(() =>
  import('./pages/customer-hub/CustomerReportsPage').then((m) => ({ default: m.CustomerReportsPage }))
);
const CustomerReportEditorPage = lazy(() =>
  import('./pages/customer-hub/CustomerReportEditorPage').then((m) => ({
    default: m.CustomerReportEditorPage,
  }))
);
const CustomerContractsPage = lazy(() =>
  import('./pages/customer-hub/CustomerContractsPage').then((m) => ({ default: m.CustomerContractsPage }))
);
const FaqPage = lazy(() => import('./pages/FaqPage').then((m) => ({ default: m.FaqPage })));
const TimeBookingPage = lazy(() =>
  import('./pages/TimeBookingPage').then((m) => ({ default: m.TimeBookingPage }))
);
const TimeProposalsPage = lazy(() =>
  import('./pages/TimeProposalsPage').then((m) => ({ default: m.TimeProposalsPage }))
);
const DnsZonesPage = lazy(() =>
  import('./pages/DnsZonesPage').then((m) => ({ default: m.DnsZonesPage }))
);
const DnsZoneDetailPage = lazy(() =>
  import('./pages/DnsZoneDetailPage').then((m) => ({ default: m.DnsZoneDetailPage }))
);
const DomainsPage = lazy(() =>
  import('./pages/DomainsPage').then((m) => ({ default: m.DomainsPage }))
);
const DomainDetailPage = lazy(() =>
  import('./pages/DomainDetailPage').then((m) => ({ default: m.DomainDetailPage }))
);
const AttendancePage = lazy(() =>
  import('./pages/AttendancePage').then((m) => ({ default: m.AttendancePage }))
);

const MyTicketsPage = lazy(() =>
  import('./pages/MyTicketsPage').then((m) => ({ default: m.MyTicketsPage }))
);
const HandoversPage = lazy(() =>
  import('./pages/HandoversPage').then((m) => ({ default: m.HandoversPage }))
);
const ChatInboxPage = lazy(() =>
  import('./pages/ChatInboxPage').then((m) => ({ default: m.ChatInboxPage }))
);
const KianWorkspacePage = lazy(() =>
  import('./pages/kian/KianWorkspacePage').then((m) => ({ default: m.KianWorkspacePage }))
);
const MyKnowledgeBasePage = lazy(() =>
  import('./pages/MyKnowledgeBasePage').then((m) => ({ default: m.MyKnowledgeBasePage }))
);
const KnowledgeBaseArticlePage = lazy(() =>
  import('./pages/KnowledgeBaseArticlePage').then((m) => ({ default: m.KnowledgeBaseArticlePage }))
);
const AdminKnowledgeBasePage = lazy(() =>
  import('./pages/admin/AdminKnowledgeBasePage').then((m) => ({ default: m.AdminKnowledgeBasePage }))
);
// KIAN-Admin: 4 Seiten zu einer Hub-Seite mit Tabs zusammengeführt.
// Die Einzelseiten werden innerhalb des Hubs lazy eingebettet.
const AdminKianHubPage = lazy(() =>
  import('./pages/admin/AdminKianHubPage').then((m) => ({ default: m.AdminKianHubPage }))
);
const CustomerPortalAdminPage = lazy(() =>
  import('./pages/admin/CustomerPortalAdminPage').then((m) => ({ default: m.CustomerPortalAdminPage }))
);
const AdminShopPage = lazy(() =>
  import('./pages/admin/AdminShopPage').then((m) => ({ default: m.AdminShopPage }))
);
const AdminEmailPreviewPage = lazy(() =>
  import('./pages/admin/AdminEmailPreviewPage').then((m) => ({ default: m.AdminEmailPreviewPage }))
);
const AdminMailIntakePage = lazy(() =>
  import('./pages/admin/AdminMailIntakePage').then((m) => ({ default: m.AdminMailIntakePage }))
);
const AdminWebhookLogsPage = lazy(() =>
  import('./pages/admin/AdminWebhookLogsPage').then((m) => ({ default: m.AdminWebhookLogsPage }))
);
const AdminEmailLogsPage = lazy(() =>
  import('./pages/admin/AdminEmailLogsPage').then((m) => ({ default: m.AdminEmailLogsPage }))
);
const AdminCallersPage = lazy(() =>
  import('./pages/admin/AdminUnknownCallersPage').then((m) => ({ default: m.AdminCallersPage }))
);
const AdminOnCallPage = lazy(() =>
  import('./pages/admin/AdminOnCallPage').then((m) => ({ default: m.AdminOnCallPage }))
);
const MyOnCallPage = lazy(() =>
  import('./pages/MyOnCallPage').then((m) => ({ default: m.MyOnCallPage }))
);
const ServiceReportApprovalPage = lazy(() =>
  import('./pages/ServiceReportApprovalPage').then((m) => ({ default: m.ServiceReportApprovalPage }))
);
const RufbereitschaftAckPage = lazy(() =>
  import('./pages/RufbereitschaftAckPage').then((m) => ({ default: m.RufbereitschaftAckPage }))
);
const AdminKianUsagePage = lazy(() =>
  import('./pages/admin/AdminKianUsagePage').then((m) => ({ default: m.AdminKianUsagePage }))
);
const AdminKianInjectionsPage = lazy(() =>
  import('./pages/admin/AdminKianInjectionsPage').then((m) => ({ default: m.AdminKianInjectionsPage }))
);
const AdminTicketSenderTriagePage = lazy(() =>
  import('./pages/admin/AdminTicketSenderTriagePage').then((m) => ({ default: m.AdminTicketSenderTriagePage }))
);
const AdminCustomerRequestsPage = lazy(() =>
  import('./pages/admin/AdminCustomerRequestsPage').then((m) => ({ default: m.AdminCustomerRequestsPage }))
);
const TeamTicketsPage = lazy(() =>
  import('./pages/TeamTicketsPage').then((m) => ({ default: m.TeamTicketsPage }))
);
const MaintenanceDashboardPage = lazy(() =>
  import('./pages/MaintenanceDashboardPage').then((m) => ({ default: m.MaintenanceDashboardPage }))
);

/**
 * Alter Quittungspfad `/notdienst/:token` → `/rufbereitschaft/:token`.
 *
 * Alarm-SMS und Glocken-Hinweise, die vor der Umbenennung erzeugt wurden, tragen den
 * alten Pfad. Ein Link, der um drei Uhr ins Leere läuft, ist hier der teuerste
 * denkbare Fehler — deshalb bleibt die Umleitung dauerhaft stehen.
 */
function LegacyAckRedirect() {
  const { token } = useParams<{ token: string }>();
  return <Navigate to={`/rufbereitschaft/${token ?? ''}`} replace />;
}

/** Alter Archiv-Pfad `/delivery-records/:id` → `/einsatz-archiv/:id`. */
function ArchiveRecordRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/einsatz-archiv/${id ?? ''}`} replace />;
}

function FullPageLoader({ label = 'Wird geladen…' }: { label?: string }) {
  return (
    <Box className="gradient-bg" pos="relative" mih="100vh" w="100%">
      <Center mih="100vh" style={{ flexDirection: 'column', gap: 16 }}>
        {/* Hex statt Theme-Token: funktioniert auch wenn Custom-Color-CSS fehlt */}
        <Loader color="#ff6600" type="bars" />
        <Text size="sm" style={{ color: '#adb5bd' }}>
          {label}
        </Text>
      </Center>
    </Box>
  );
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return <FullPageLoader />;
  }

  if (!isAuthenticated) {
    const returnTo = location.pathname + location.search;
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuthStore();

  if (isLoading) {
    return <FullPageLoader />;
  }

  if (user?.role !== 'ADMIN') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function TeamleadOrAdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuthStore();

  if (isLoading) {
    return <FullPageLoader />;
  }

  if (user?.role !== 'ADMIN' && user?.role !== 'TEAMLEAD') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export function App() {
  const checkAuth = useAuthStore((s) => s.checkAuth);
  const [appReady, setAppReady] = useState(false);
  const routerLocation = useLocation();
  // Kunden-Host (kundenportal.*): nur das Kundenportal ausliefern. Techniker-Pfade
  // leiten auf /customer/dashboard um; /customer-approve (Angebots-Freigabe per
  // Mail-Link) bleibt auf beiden Hosts erreichbar.
  const onCustomerHost = isCustomerPortalHost();
  const isCustomerPath =
    /^\/customer(\/|$)/.test(routerLocation.pathname) ||
    routerLocation.pathname.startsWith('/customer-approve') ||
    // Abnahme-Link eines Serviceberichts: geht per Mail an Kunden und muss auf
    // beiden Hosts aufgehen — eine Umleitung ins Kundenportal-Dashboard wäre für
    // den Empfänger eine Sackgasse (er hat dort keinen Zugang).
    routerLocation.pathname.startsWith('/servicebericht/');

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        if (!useAuthStore.persist.hasHydrated()) {
          await useAuthStore.persist.rehydrate();
        }
      } catch (e) {
        console.error('Auth-Speicher konnte nicht geladen werden:', e);
      }
      if (cancelled) return;

      try {
        await checkAuth();
      } catch (e) {
        // checkAuth-Fehler = kein Token oder Netzwerkfehler; Auth-Guard übernimmt Weiterleitung
        console.error('checkAuth fehlgeschlagen:', e);
      } finally {
        if (!cancelled) setAppReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [checkAuth]);

  if (onCustomerHost && !isCustomerPath) {
    return <Navigate to="/customer/dashboard" replace />;
  }

  if (!appReady) {
    return <FullPageLoader label="Sitzung wird vorbereitet…" />;
  }

  return (
    <>
      <Suspense fallback={<FullPageLoader label="Ansicht wird geladen…" />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/customer-approve" element={<CustomerApprovePage />} />
          {/* Quittungs-/Stempelseite der Notfall-SMS — ohne Anmeldung, Token in der URL */}
          <Route path="/rufbereitschaft/:token" element={<RufbereitschaftAckPage />} />
          {/* Abnahme eines Serviceberichts durch den Ansprechpartner — ohne Anmeldung */}
          <Route path="/servicebericht/:token" element={<ServiceReportApprovalPage />} />
          {/* Alter Pfad: SMS und Glocken-Hinweise, die vor der Umbenennung erzeugt wurden,
              dürfen nicht ins Leere laufen — ein toter Alarm-Link ist der teuerste Fehler hier. */}
          <Route path="/notdienst/:token" element={<LegacyAckRedirect />} />

          <Route
            path="/"
            element={
              <PrivateRoute>
                <AppLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="dispatch" element={<Navigate to="/tickets?view=dispatch" replace />} />
            <Route path="tickets/new" element={<NewTicketPage />} />
            <Route path="tickets/board" element={<Navigate to="/tickets?view=board" replace />} />
            <Route path="infrastructure" element={<InfrastructureViewerPage />} />
            <Route path="inventory" element={<InventoryPage />} />
            <Route path="inventory/articles" element={<ArticlesPage />} />
            <Route
              path="inventory/article-review"
              element={
                <AdminRoute>
                  <ArticleReviewPage />
                </AdminRoute>
              }
            />
            <Route path="inventory/movements" element={<MovementsPage />} />
            <Route path="inventory/issue" element={<StockIssuePage />} />
            <Route path="inventory/search" element={<StockSearchPage />} />
            <Route path="inventory/transfer" element={<StockTransferPage />} />
            <Route path="inventory/valuation" element={<ValuationPage />} />
            <Route path="inventory/low-stock" element={<LowStockPage />} />
            <Route path="inventory/serials" element={<SerialHistoryPage />} />
            <Route path="inventory/returns" element={<StockReturnPage />} />
            <Route path="inventory/returns/review" element={<StockReturnReviewPage />} />
            <Route path="inventory/delivery-orders" element={<DeliveryOrderListPage />} />
            <Route path="inventory/delivery-orders/:id" element={<DeliveryOrderDetailPage />} />
            <Route path="inventory/warehouses/:warehouseId" element={<WarehouseBinsPage />} />
            <Route path="inventory/bins/:binId" element={<BinOnHandPage />} />
            <Route path="inventory/inventory-start" element={<InventoryStartPage />} />
            <Route path="inventory/inventory-start/:warehouseId" element={<InventoryStartPage />} />
            <Route path="inventory/inventory-session/:sessionId" element={<InventorySessionPage />} />
            <Route path="inventory/inventory-session/:sessionId/review" element={<InventoryReviewPage />} />
            <Route path="goods-receipts" element={<GoodsReceiptsListPage />} />
            <Route path="goods-receipts/new" element={<GoodsReceiptWizardPage />} />
            <Route path="goods-receipts/:id" element={<GoodsReceiptWizardPage />} />
            <Route path="purchase-orders" element={<PurchaseOrdersListPage />} />
            <Route path="purchase-orders/new" element={<PurchaseOrderWizardPage />} />
            <Route path="purchase-orders/:id" element={<PurchaseOrderWizardPage />} />
            <Route path="procurement" element={<ProcurementPipelinePage />} />
            <Route path="procurement/:id" element={<PipelineDetailPage />} />
            <Route path="einsatz-archiv" element={<SubmissionArchivePage />} />
            <Route path="einsatz-archiv/:id" element={<ArchiveRecordPage />} />
            {/* Bis v1.344.0 lag das Archiv unter „delivery-records" — ein Pfad, unter dem
                niemand Serviceberichte vermutet. Weitergeleitete Links sollen weiter gehen. */}
            <Route path="delivery-records" element={<Navigate to="/einsatz-archiv" replace />} />
            <Route path="delivery-records/:id" element={<ArchiveRecordRedirect />} />
            <Route path="service-report" element={<ServiceReportPage />} />
            <Route path="service-report/:id" element={<ServiceReportPage />} />
            <Route path="delivery-report" element={<DeliveryReportPage />} />
            <Route path="delivery-report/:id" element={<DeliveryReportPage />} />
            <Route path="submissions" element={<SubmissionsPage />} />
            <Route path="trash" element={<TrashPage />} />
            <Route path="my-trips" element={<MyTripsPage />} />
            <Route path="tickets" element={<TicketsHubPage />} />
            <Route
              path="tickets/trash"
              element={
                <AdminRoute>
                  <TicketTrashPage />
                </AdminRoute>
              }
            />
            {/* Geteilter Deeplink: /t/T26-02199 — kurz genug für Chat-Nachrichten. */}
            <Route path="t/:ref" element={<TicketDeepLinkPage />} />
            <Route path="briefing" element={<BriefingPage />} />
            <Route path="statistik" element={<StatisticsPage />} />
            <Route path="reminders" element={<TodosPage />} />
            <Route path="reminders/overview" element={<TodoOverviewPage />} />
            <Route path="rufbereitschaft" element={<MyOnCallPage />} />
            <Route path="notdienst" element={<Navigate to="/rufbereitschaft" replace />} />
            <Route path="handovers" element={<HandoversPage />} />
            <Route path="chats" element={<ChatInboxPage />} />
            <Route path="my-tickets" element={<Navigate to="/tickets?tab=mine" replace />} />
            <Route path="team-tickets" element={<Navigate to="/tickets?tab=team" replace />} />
            <Route path="contact-suggestions" element={<ContactSuggestionsPage />} />
            <Route path="contacts" element={<ContactDirectoryPage />} />
            <Route path="maintenance" element={<MaintenanceDashboardPage />} />
            <Route path="kian" element={<KianWorkspacePage />} />
            {/* Früherer Pfad der Chat-Übersicht — Lesezeichen und alte Links landen im KIAN-Fenster */}
            <Route path="assistant" element={<Navigate to="/kian" replace />} />
            <Route path="knowledge-base" element={<MyKnowledgeBasePage />} />
            <Route path="knowledge-base/:slug" element={<KnowledgeBaseArticlePage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="time-booking" element={<TimeBookingPage />} />
            <Route path="zeitvorschlaege" element={<TimeProposalsPage />} />
            <Route path="anwesenheit" element={<AttendancePage />} />
            <Route path="dns-zonen" element={<DnsZonesPage />} />
            <Route path="dns-zonen/:id" element={<DnsZoneDetailPage />} />
            <Route path="domains" element={<DomainsPage />} />
            <Route path="domains/:id" element={<DomainDetailPage />} />

            <Route path="technician-requests" element={<TechnicianRequestsPage />} />
            <Route path="projects" element={<ProjectsPage />} />
            <Route path="projects/templates" element={<ProjectTemplatesPage />} />
            <Route path="projects/:id" element={<ProjectDetailPage />} />
            <Route path="portal-access-requests" element={<PortalAccessRequestsPage />} />
            <Route path="appointments" element={<TechnicianAppointmentsPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="tools" element={<ToolsPage />} />
            <Route path="help/faq" element={<FaqPage />} />
            <Route path="customer-hub" element={<CustomerHubPage />} />
            <Route path="customer-hub/quote/:workflowId" element={<QuoteWorkflowPage />} />
            <Route path="customer-hub/warranty/:workflowId" element={<WarrantyWorkflowPage />} />
            <Route path="customer-hub/order/:workflowId" element={<OrderWorkflowPage />} />
            <Route
              path="customer-hub/bestellung/:workflowId"
              element={<CustomerOrderWorkflowPage />}
            />
            <Route path="customer-hub/customer" element={<CustomerDetailPage />} />
            <Route path="customer-hub/contracts" element={<CustomerContractsPage />} />
            <Route path="customer-hub/reports" element={<CustomerReportsPage />} />
            <Route path="customer-hub/reports/:id" element={<CustomerReportEditorPage />} />

            <Route
              path="admin/belege"
              element={
                <AdminRoute>
                  <AdminBelegePage />
                </AdminRoute>
              }
            />
            <Route
              path="auftraege"
              element={
                <PrivateRoute>
                  <OrdersPage />
                </PrivateRoute>
              }
            />
            <Route
              path="admin/users"
              element={
                <AdminRoute>
                  <AdminUsersPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/submissions"
              element={
                <TeamleadOrAdminRoute>
                  <AdminSubmissionsPage />
                </TeamleadOrAdminRoute>
              }
            />
            <Route
              path="admin/settings"
              element={
                <AdminRoute>
                  <AdminSettingsPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/trips"
              element={
                <AdminRoute>
                  <AdminTripsPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/manual-trips"
              element={
                <AdminRoute>
                  <AdminManualTripsPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/mail-intake"
              element={
                <AdminRoute>
                  <AdminMailIntakePage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/sync"
              element={
                <AdminRoute>
                  <AdminSyncPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/rufbereitschaft"
              element={
                <AdminRoute>
                  <AdminOnCallPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/notdienst"
              element={<Navigate to="/admin/rufbereitschaft" replace />}
            />
            <Route
              path="admin/leistungsjournal"
              element={
                <AdminRoute>
                  <AdminBillingJournalPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/weclapp-mirror"
              element={
                <AdminRoute>
                  <AdminWeclappMirrorPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/technician-requests"
              element={
                <AdminRoute>
                  <AdminTechnicianRequestsPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/knowledge-base"
              element={
                <AdminRoute>
                  <AdminKnowledgeBasePage />
                </AdminRoute>
              }
            />
            {/* KIAN-Admin: Einzelrouten leiten auf den Hub mit dem passenden Tab um */}
            <Route
              path="admin/context-strategies"
              element={<Navigate to="/admin/kian?tab=strategies" replace />}
            />
            <Route
              path="admin/kian-analytics"
              element={<Navigate to="/admin/kian?tab=analytics" replace />}
            />
            <Route
              path="admin/context-metrics"
              element={<Navigate to="/admin/kian?tab=metrics" replace />}
            />
            <Route
              path="admin/kian"
              element={
                <AdminRoute>
                  <AdminKianHubPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/customer-portal"
              element={
                <AdminRoute>
                  <CustomerPortalAdminPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/tools"
              element={
                <AdminRoute>
                  <AdminToolsPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/email-previews"
              element={
                <AdminRoute>
                  <AdminEmailPreviewPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/webhook-logs"
              element={
                <AdminRoute>
                  <AdminWebhookLogsPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/email-logs"
              element={
                <AdminRoute>
                  <AdminEmailLogsPage />
                </AdminRoute>
              }
            />
            <Route path="admin/unknown-callers" element={<Navigate to="/admin/callers" replace />} />
            <Route
              path="admin/callers"
              element={
                <PrivateRoute>
                  <AdminCallersPage />
                </PrivateRoute>
              }
            />
            <Route
              path="admin/ticket-sender-triage"
              element={
                <PrivateRoute>
                  <AdminTicketSenderTriagePage />
                </PrivateRoute>
              }
            />
            <Route
              path="admin/kundenanlagen"
              element={
                <PrivateRoute>
                  <AdminCustomerRequestsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="admin/address-suggestions"
              element={
                <TeamleadOrAdminRoute>
                  <AddressSuggestionsPage />
                </TeamleadOrAdminRoute>
              }
            />
            <Route
              path="admin/kian-usage"
              element={
                <AdminRoute>
                  <AdminKianUsagePage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/kian-injections"
              element={
                <TeamleadOrAdminRoute>
                  <AdminKianInjectionsPage />
                </TeamleadOrAdminRoute>
              }
            />
            <Route
              path="admin/shop"
              element={
                <AdminRoute>
                  <AdminShopPage />
                </AdminRoute>
              }
            />
          </Route>

          {/* Kunden-Portal */}
          <Route path="/customer/*" element={<CustomerPortalRoutes />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>

      {/* Global Ticket Cockpit Drawer */}
      <TicketCockpitDrawer />

      {/* Global Customer Detail Drawer */}
      <GlobalCustomerDrawer />

      <KianFlyoutRoot />
    </>
  );
}
